import { FC, useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { SpocPremiseViewProps } from "./spocPremise.interfaces";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContactPhone as ContactPhoneIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import { useSpocHook, useDeleteSpocHook } from "../../../hooks/useSpocHook";
import {
  TSpoc,
  TSpocStatus,
} from "../../../apis/spocPremise/spocPremise.interfaces";
import { useInstituteContext } from "../../../context/institute.context";
import ManageSpocDialog from "./manageSpocDialog";
import { logger, pages } from "../../../utils/common";
import PremiseListView from "./premiseList.view";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";

const SPOC_PREMISE_TAB_SLUGS = ["spocs", "premises"] as const;
type SpocPremiseTabSlug = (typeof SPOC_PREMISE_TAB_SLUGS)[number];
const isSpocPremiseTabSlug = (value: string): value is SpocPremiseTabSlug =>
  (SPOC_PREMISE_TAB_SLUGS as readonly string[]).includes(value);

const SpocPremiseView: FC<SpocPremiseViewProps> = () => {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const { selectedInstitute } = useInstituteContext();
  const { data, isLoading, error } = useSpocHook();
  const deleteSpocMutation = useDeleteSpocHook();
  const { control, watch } = useForm({ defaultValues: { search: "" } });
  const searchQuery = watch("search");
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [editingSpoc, setEditingSpoc] = useState<TSpoc | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [spocToDelete, setSpocToDelete] = useState<TSpoc | null>(null);

  const activeTab = useMemo(() => {
    if (tab && isSpocPremiseTabSlug(tab)) {
      return SPOC_PREMISE_TAB_SLUGS.indexOf(tab);
    }
    return 0;
  }, [tab]);

  useEffect(() => {
    if (tab && !isSpocPremiseTabSlug(tab)) {
      navigate(`${pages.spocPremise.path}/spocs`, { replace: true });
    }
  }, [tab, navigate]);

  const filteredSpocs = useMemo(() => {
    if (!data) return [];

    return data.filter((spoc) => {
      const matchesSearch =
        spoc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        spoc.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
        spoc.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        spoc.phone.includes(searchQuery);

      return matchesSearch;
    });
  }, [data, searchQuery]);

  const getStatusChip = (status: TSpocStatus) => {
    return (
      <Chip
        label={status === "primary" ? "Primary" : "Secondary"}
        color={status === "primary" ? "primary" : "default"}
        size="small"
        icon={status === "primary" ? <StarIcon /> : undefined}
      />
    );
  };

  const handleAddSpoc = () => {
    setEditingSpoc(null);
    setManageDialogOpen(true);
  };

  const handleEditSpoc = (spoc: TSpoc) => {
    setEditingSpoc(spoc);
    setManageDialogOpen(true);
  };

  const handleDeleteSpoc = (spoc: TSpoc) => {
    setSpocToDelete(spoc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!spocToDelete) return;

    try {
      await deleteSpocMutation.mutateAsync(spocToDelete.id);
      logger.show_success("SPOC deleted successfully!");
      setDeleteDialogOpen(false);
      setSpocToDelete(null);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to delete SPOC. Please try again.",
      );
    }
  };

  const handleManageDialogClose = () => {
    setManageDialogOpen(false);
    setEditingSpoc(null);
  };

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.spocPremise} />
        <Box p={3} data-testid="spoc-premise-view">
          <MainTitle title="SPOC & Premise Management" />
          <Alert severity="info">
            Please select an institute from the dashboard to view SPOCs and
            Premises.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.spocPremise} />

      <Grid container spacing={3} data-testid="spoc-premise-view">
        <Grid item xs={12}>
          <MainTitle title="SPOC & Premise Management" />
          <Box
            sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}
            data-testid="spoc-premise-tabs-container"
          >
            <Tabs
              value={activeTab}
              onChange={(_, newValue) =>
                navigate(`${pages.spocPremise.path}/${SPOC_PREMISE_TAB_SLUGS[newValue]}`)
              }
              data-testid="spoc-premise-tabs"
            >
              <Tab label="SPOCs" data-testid="spocs-tab" />
              <Tab label="Premises" data-testid="premises-tab" />
            </Tabs>
          </Box>
        </Grid>
      </Grid>

      {activeTab === 0 && (
        <>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
              data-testid="spoc-loading-container"
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box p={3} data-testid="spoc-error-container">
              <Alert severity="error">
                Failed to load SPOCs. Please try again later.
              </Alert>
            </Box>
          ) : (
            <Grid container spacing={3} data-testid="spoc-list-container">
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ my: 2 }}
                  data-testid="spoc-header"
                >
                  <Typography variant="h6">SPOC Management</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddSpoc}
                    size="large"
                    data-testid="add-spoc-button"
                  >
                    Add SPOC
                  </Button>
                </Box>
              </Grid>

              {/* Search */}
              <Grid item xs={12}>
                <Card data-testid="spoc-search-card">
                  <CardContent>
                    <ControlledTextField
                      control={control}
                      name="search"
                      id="spoc-search"
                      label="Search"
                      isGridItem={false}
                      fullWidth
                      placeholder="Search by name, role, email, or phone..."
                      inputProps={{ "data-testid": "spoc-search-input" }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* SPOC List */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  SPOCs ({filteredSpocs.length})
                </Typography>
              </Grid>

              {filteredSpocs.length === 0 ? (
                <Grid item xs={12}>
                  <Card data-testid="spoc-empty-state">
                    <CardContent>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        minHeight="200px"
                        gap={2}
                        data-testid="spoc-empty-content"
                      >
                        <ContactPhoneIcon
                          sx={{ fontSize: 64, color: "text.secondary" }}
                        />
                        <Typography variant="h6" color="text.secondary">
                          {data?.length === 0
                            ? "No SPOCs configured"
                            : "No SPOCs match your search criteria"}
                        </Typography>
                        {data?.length === 0 && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Get started by adding your first SPOC.
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={handleAddSpoc}
                              data-testid="add-spoc-empty-button"
                            >
                              Add SPOC
                            </Button>
                          </>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    data-testid="spoc-table-container"
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSpocs.map((spoc) => (
                          <TableRow
                            key={spoc.id}
                            hover
                            data-testid={`spoc-row-${spoc.id}`}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {spoc.status === "primary" && (
                                  <StarIcon color="primary" fontSize="small" />
                                )}
                                <Typography
                                  variant="body2"
                                  fontWeight={
                                    spoc.status === "primary"
                                      ? "bold"
                                      : "normal"
                                  }
                                >
                                  {spoc.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{spoc.role}</TableCell>
                            <TableCell>{spoc.email}</TableCell>
                            <TableCell>{spoc.phone}</TableCell>
                            <TableCell>{getStatusChip(spoc.status)}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleEditSpoc(spoc)}
                                color="primary"
                                aria-label="edit"
                                data-testid={`edit-spoc-${spoc.id}`}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteSpoc(spoc)}
                                color="error"
                                aria-label="delete"
                                data-testid={`delete-spoc-${spoc.id}`}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </Grid>
          )}
        </>
      )}

      {activeTab === 1 && <PremiseListView />}

      <ManageSpocDialog
        open={manageDialogOpen}
        onClose={handleManageDialogClose}
        spoc={editingSpoc}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSpocToDelete(null);
        }}
        data-testid="delete-spoc-dialog"
      >
        <DialogTitle>Delete SPOC</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete SPOC "{spocToDelete?.name}"? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSpocToDelete(null);
            }}
            disabled={deleteSpocMutation.isPending}
            data-testid="cancel-delete-spoc-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteSpocMutation.isPending}
            startIcon={
              deleteSpocMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-spoc-button"
          >
            {deleteSpocMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SpocPremiseView;
